import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Ban, Plus, Trash2, RefreshCcw } from "lucide-react";
import { Modal, Field, FieldTextarea } from "./AdminBusinesses";

export default function AdminBannedEmails() {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/banned-emails`);
    setItems(data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const addBan = async (form) => {
    try {
      await api.post(`/admin/banned-emails`, form);
      toast.success("Email banned.");
      setAdding(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };
  const unban = async (e) => {
    if (!window.confirm(`Lift ban for ${e.email}?`)) return;
    await api.delete(`/admin/banned-emails/${encodeURIComponent(e.email)}`);
    toast.success("Email unbanned.");
    load();
  };

  return (
    <div data-testid="admin-banned-emails-page">
      <div className="flex items-center justify-between gap-2">
        <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
          <Ban className="w-5 h-5" /> Banned Emails
        </h1>
        <div className="flex items-center gap-2">
          <button onClick={load} className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 inline-flex items-center gap-1">
            <RefreshCcw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button data-testid="ban-add-button" onClick={() => setAdding(true)}
                  className="px-3 py-1.5 text-sm rounded-lg bg-rose-600 text-white inline-flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> Ban an email
          </button>
        </div>
      </div>
      <p className="text-sm text-slate-600 mt-1">
        Banned emails are blocked from registering as customer or business. Existing users with these emails are deactivated.
      </p>
      <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left p-2">Email</th>
              <th className="text-left p-2">Reason</th>
              <th className="text-left p-2">Banned at</th>
              <th className="text-right p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.email} className="border-t border-slate-100">
                <td className="p-2 font-medium">{e.email}</td>
                <td className="p-2 text-slate-600">{e.reason || "-"}</td>
                <td className="p-2 text-slate-600 text-xs">{(e.banned_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="p-2 text-right">
                  <button data-testid={`ban-remove-${e.email}`} onClick={() => unban(e)}
                          className="text-xs px-2 py-1 rounded border border-emerald-300 text-emerald-700 inline-flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> Unban
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-slate-500">No banned emails.</td></tr>}
          </tbody>
        </table>
      </div>
      {adding && <AddBanModal onClose={() => setAdding(false)} onSave={addBan} />}
    </div>
  );
}

function AddBanModal({ onClose, onSave }) {
  const [form, setForm] = useState({ email: "", reason: "" });
  return (
    <Modal title="Ban an email" onClose={onClose} testId="ban-add-modal">
      <div className="space-y-3 text-sm">
        <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="ban-add-email" />
        <FieldTextarea label="Reason" value={form.reason} onChange={(v) => setForm({ ...form, reason: v })} />
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-slate-300">Cancel</button>
        <button data-testid="ban-add-submit" onClick={() => onSave(form)} className="px-3 py-1.5 text-sm rounded bg-rose-600 text-white">Ban email</button>
      </div>
    </Modal>
  );
}
